import React, { useState } from "react";
import { triggerDiwaliCrackers } from "./diwaliCrackers";
import "./OxyDiwaliGoogleDoodle.css";

const OxyDiwaliGoogleDoodle = ({ onClick }) => {
  const [animKey, setAnimKey] = useState(0);

  const handleReplay = (e) => {
    // Increment animKey on click/tap to trigger a fresh celebration replay
    setAnimKey((prev) => prev + 1);

    // Burst screen crackers from click coordinate
    try {
      const clientX = e?.clientX ?? (typeof window !== "undefined" ? window.innerWidth * 0.75 : 300);
      const clientY = e?.clientY ?? 40;
      triggerDiwaliCrackers({ originX: clientX, originY: clientY, duration: 4500 });
    } catch {
      // Ignore if event coords unavailable
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="oxy-diwali-doodle-wrapper"
      onClick={handleReplay}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleReplay(e);
        }
      }}
      title="Happy Diwali from OxyLoans! Click to burst crackers & view festive wishes"
      aria-label="Happy Diwali from OxyLoans! Click to burst crackers & view festive wishes"
    >
      {/* ===================================================================
          1. DESKTOP & TABLET DIWALI DOODLE (> 767px)
          Diya + "Happy Diwali OxyLoans" + Diwali Cracker Rocket (178x40)
          =================================================================== */}
      <div className="oxy-diwali-desktop" key={`desktop-${animKey}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 178 40"
          className="oxy-diwali-svg"
          aria-hidden="true"
        >
          <defs>
            {/* Diya Bowl Gradient */}
            <linearGradient id="diyaBowlGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="35%" stopColor="#ea580c" />
              <stop offset="80%" stopColor="#9a3412" />
              <stop offset="100%" stopColor="#7c2d12" />
            </linearGradient>

            {/* Diya Rim Gradient */}
            <linearGradient id="diyaRimGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#fef08a" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>

            {/* Flame Outer Gradient */}
            <linearGradient id="flameOuterGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#dc2626" />
              <stop offset="35%" stopColor="#ea580c" />
              <stop offset="75%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#fef08a" />
            </linearGradient>

            {/* Flame Inner Gradient */}
            <linearGradient id="flameInnerGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#facc15" />
              <stop offset="60%" stopColor="#fef08a" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>

            {/* Rocket Cracker Body Gradient */}
            <linearGradient id="rocketBodyGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="35%" stopColor="#f59e0b" />
              <stop offset="70%" stopColor="#10b981" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>

          {/* Background Cracker Fireworks Burst */}
          <g className="cracker-burst" transform="translate(142, 6)">
            <line x1="0" y1="-5" x2="0" y2="-8" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="4" y1="-3" x2="7" y2="-5" stroke="#f59e0b" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="5" y1="2" x2="8" y2="4" stroke="#10b981" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="0" y1="5" x2="0" y2="8" stroke="#3b82f6" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="-4" y1="3" x2="-7" y2="5" stroke="#8b5cf6" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="-5" y1="-2" x2="-8" y2="-4" stroke="#f59e0b" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="0" cy="0" r="1.5" fill="#fde047" />
          </g>

          {/* Sparkling Stars */}
          <g className="diwali-sparkle diwali-sp-1" transform="translate(3, 8)">
            <path d="M 0,-3.5 Q 0,0 3.5,0 Q 0,0 0,3.5 Q 0,0 -3.5,0 Q 0,0 0,-3.5 Z" fill="#0284c7" />
          </g>
          <g className="diwali-sparkle diwali-sp-3" transform="translate(38, 33)">
            <path d="M 0,-2.5 Q 0,0 2.5,0 Q 0,0 0,2.5 Q 0,0 -2.5,0 Q 0,0 0,-2.5 Z" fill="#059669" />
          </g>

          {/* 1. Diya Group */}
          <g>
            <circle cx="20" cy="12" r="10" fill="#fde047" opacity="0.6" className="diwali-flame-aura" />
            <g className="diwali-flame-core">
              <path
                d="M 20 2 C 16 7, 13 13, 14 18 C 15 22, 25 22, 26 18 C 27 13, 24 7, 20 2 Z"
                fill="url(#flameOuterGrad)"
              />
              <path
                d="M 20 6 C 18 9.5, 16 13.5, 17 17 C 17.5 19.5, 22.5 19.5, 23 17 C 24 13.5, 22 9.5, 20 6 Z"
                fill="url(#flameInnerGrad)"
              />
              <path d="M 20 18 L 20 21" stroke="#451a03" strokeWidth="1.3" strokeLinecap="round" />
            </g>
            <ellipse cx="20" cy="37" rx="7" ry="1.6" fill="#7c2d12" />
            <path
              d="M 6 23 C 6 32, 13 36, 20 36 C 27 36, 34 32, 34 23 C 28 25, 25 22.5, 20 22.5 C 15 22.5, 12 25, 6 23 Z"
              fill="url(#diyaBowlGrad)"
            />
            <path
              d="M 10 26.5 Q 20 31.5 30 26.5"
              fill="none"
              stroke="#fbbf24"
              strokeWidth="0.8"
              opacity="0.9"
            />
            <ellipse cx="20" cy="22.5" rx="14" ry="3" fill="url(#diyaRimGrad)" stroke="#9a3412" strokeWidth="0.5" />
            <ellipse cx="20" cy="22.5" rx="9" ry="1.6" fill="#7c2d12" />
          </g>

          {/* 2. Typography */}
          <g transform="translate(43, 0)">
            <text
              x="0"
              y="17"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="12"
              fontWeight="800"
              fill="#b45309"
              letterSpacing="0.01em"
            >
              Happy Diwali ✨
            </text>
            <text
              x="0"
              y="33"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="15.5"
              fontWeight="900"
              letterSpacing="-0.02em"
            >
              <tspan fill="#1d4ed8">Oxy</tspan>
              <tspan fill="#059669">Loans</tspan>
            </text>
            <path
              d="M 0 37 Q 48 39.5 94 37"
              fill="none"
              stroke="#ea580c"
              strokeWidth="1.6"
              strokeLinecap="round"
              opacity="0.9"
            />
            <circle cx="96" cy="36.5" r="1.3" fill="#b45309" />
          </g>

          {/* 3. Diwali Cracker Rocket with Sparkler Trail */}
          <g transform="translate(155, 6)">
            {/* Guide Stick */}
            <line x1="-3" y1="28" x2="10" y2="6" stroke="#92400e" strokeWidth="1.4" strokeLinecap="round" />
            <g transform="rotate(-32, 10, 14)">
              {/* Rocket Body */}
              <rect x="5" y="10" width="8" height="18" rx="2" fill="url(#rocketBodyGrad)" stroke="#b45309" strokeWidth="0.5" />
              <line x1="5" y1="14" x2="13" y2="14" stroke="#fde047" strokeWidth="0.8" />
              <line x1="5" y1="19" x2="13" y2="19" stroke="#ffffff" strokeWidth="0.8" />
              <line x1="5" y1="24" x2="13" y2="24" stroke="#fde047" strokeWidth="0.8" />
              {/* Nose Cone */}
              <polygon points="9,2 4,10 14,10" fill="#dc2626" stroke="#991b1b" strokeWidth="0.5" />
              {/* Sparks from Fuse */}
              <g className="rocket-sparkle" transform="translate(9, 29)">
                <circle cx="0" cy="1" r="1.8" fill="#fbbf24" />
                <path d="M -2,3 L -4,7 M 2,3 L 4,7 M 0,3 L 0,8" stroke="#ea580c" strokeWidth="1.2" strokeLinecap="round" />
              </g>
            </g>
            {/* Twinkling star sparks around rocket */}
            <g className="rocket-sparkle" transform="translate(14, 0)">
              <path d="M 0,-3 Q 0,0 3,0 Q 0,0 0,3 Q 0,0 -3,0 Q 0,0 0,-3 Z" fill="#f59e0b" />
            </g>
            <g className="rocket-sparkle" transform="translate(2, 2)">
              <path d="M 0,-2.5 Q 0,0 2.5,0 Q 0,0 0,2.5 Q 0,0 -2.5,0 Q 0,0 0,-2.5 Z" fill="#10b981" />
            </g>
          </g>
        </svg>
      </div>

      {/* ===================================================================
          2. STANDARD MOBILE DIWALI DOODLE (381px - 767px)
          Diya + "Happy Diwali OxyLoans" + Mini Rocket (148x32)
          =================================================================== */}
      <div className="oxy-diwali-mobile-standard" key={`mob-std-${animKey}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 148 32"
          className="oxy-diwali-svg-mob-standard"
          aria-hidden="true"
        >
          <g transform="translate(1, 0)">
            <circle cx="15" cy="9" r="8" fill="#fde047" opacity="0.6" />
            <g className="diwali-flame-core">
              <path
                d="M 15 1 C 12 5, 10 9, 10.5 13 C 11.5 16, 18.5 16, 19.5 13 C 20 9, 18 5, 15 1 Z"
                fill="#ea580c"
              />
              <path
                d="M 15 4 C 13.5 7, 12 10, 13 13 C 13.5 15, 16.5 15, 17 13 C 18 10, 16.5 7, 15 4 Z"
                fill="#fef08a"
              />
              <path d="M 15 13 L 15 15" stroke="#451a03" strokeWidth="1" strokeLinecap="round" />
            </g>
            <ellipse cx="15" cy="29" rx="5" ry="1.2" fill="#7c2d12" />
            <path
              d="M 5 18 C 5 25, 10 28, 15 28 C 20 28, 25 25, 25 18 Z"
              fill="#d97706"
            />
            <ellipse cx="15" cy="17.5" rx="10" ry="2.2" fill="#fef08a" stroke="#9a3412" strokeWidth="0.5" />
          </g>

          <g transform="translate(34, 0)">
            <text
              x="0"
              y="13"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="9.5"
              fontWeight="800"
              fill="#b45309"
            >
              Happy Diwali ✨
            </text>
            <text
              x="0"
              y="26"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="12.5"
              fontWeight="900"
            >
              <tspan fill="#1d4ed8">Oxy</tspan>
              <tspan fill="#059669">Loans</tspan>
            </text>
            <path
              d="M 0 29 Q 38 31 76 29"
              fill="none"
              stroke="#ea580c"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.9"
            />
          </g>

          {/* Mini Rocket on right */}
          <g transform="translate(126, 4)">
            <line x1="-2" y1="22" x2="8" y2="4" stroke="#92400e" strokeWidth="1.2" />
            <g transform="rotate(-30, 8, 10)">
              <rect x="5" y="6" width="6" height="14" rx="1.5" fill="#ef4444" />
              <polygon points="8,1 4,6 12,6" fill="#f59e0b" />
            </g>
            <circle cx="14" cy="5" r="1.5" fill="#10b981" />
          </g>
        </svg>
      </div>

      {/* ===================================================================
          3. ULTRA-COMPACT MOBILE DIWALI DOODLE (<= 380px)
          Diya + "Happy Diwali" + Cracker Spark (78x28)
          =================================================================== */}
      <div className="oxy-diwali-mobile-compact" key={`mob-cmp-${animKey}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 78 28"
          className="oxy-diwali-svg-mob-compact"
          aria-hidden="true"
        >
          <g transform="translate(1, 0)">
            <circle cx="12" cy="8" r="6" fill="#fde047" opacity="0.6" />
            <g className="diwali-flame-core">
              <path
                d="M 12 1 C 9.5 4, 8 7.5, 8.5 11 C 9.5 13.5, 14.5 13.5, 15.5 11 C 16 7.5, 14.5 4, 12 1 Z"
                fill="#ea580c"
              />
              <path
                d="M 12 4 C 11 6, 10 8.5, 10.5 11 C 11 12.5, 13 12.5, 13.5 11 C 14 8.5, 13 6, 12 4 Z"
                fill="#fef08a"
              />
            </g>
            <path
              d="M 4 15 C 4 21, 8 24, 12 24 C 16 24, 20 21, 20 15 Z"
              fill="#d97706"
            />
            <ellipse cx="12" cy="14.5" rx="8" ry="1.8" fill="#fef08a" stroke="#9a3412" strokeWidth="0.5" />
          </g>

          <g transform="translate(24, 0)">
            <text
              x="0"
              y="11"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="8.5"
              fontWeight="800"
              fill="#b45309"
            >
              Happy
            </text>
            <text
              x="0"
              y="22"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
              fontSize="10"
              fontWeight="900"
              fill="#ea580c"
            >
              Diwali ✨
            </text>
          </g>

          <g className="rocket-sparkle" transform="translate(70, 7)">
            <path d="M 0,-2.5 Q 0,0 2.5,0 Q 0,0 0,2.5 Q 0,0 -2.5,0 Q 0,0 0,-2.5 Z" fill="#f59e0b" />
          </g>
        </svg>
      </div>
    </div>
  );
};

export default OxyDiwaliGoogleDoodle;
